import type { WorkflowState } from "fond/hooks/useCustomWorkflow";
import { LayerId, LayerIds } from "fond/types";

type Timestamp = string | null | undefined;

export type StalenessInput = {
  /** ISO last-modified time of each planner layer, keyed by layer ID. */
  layerLastModified: Partial<Record<string, string>> | undefined;
  /** End time of the latest COMPLETE UG generation execution. */
  ugGenerationEndTime: Timestamp;
  /** End time of the latest COMPLETE leadin generation execution. */
  leadinGenerationEndTime: Timestamp;
  /** End time of the latest COMPLETE legacy data prep execution. */
  legacyDataPrepEndTime: Timestamp;
  architectureLastModifiedAt: Timestamp;
};

export type Staleness = {
  ugStale: boolean;
  leadinsStale: boolean;
};

const UG_INPUT_LAYER_IDS: LayerId[] = [LayerIds.inAddress, LayerIds.inBaseDataStreet, LayerIds.inParcel];

const LEADIN_INPUT_LAYER_IDS: LayerId[] = [...UG_INPUT_LAYER_IDS, LayerIds.inStreet, LayerIds.inStreetPoints, LayerIds.inSpan, LayerIds.inPole];

const toMillis = (timestamp: Timestamp): number | null => {
  if (!timestamp) return null;
  const millis = Date.parse(timestamp);
  return Number.isNaN(millis) ? null : millis;
};

const latest = (...timestamps: Timestamp[]): number | null => {
  const millis = timestamps.map(toMillis).filter((value): value is number => value !== null);
  return millis.length > 0 ? Math.max(...millis) : null;
};

/** Return the execution's end time if it completed successfully. */
export const getCompletedEndTime = (state: WorkflowState, endTime: Timestamp): string | null => (state === "COMPLETE" && endTime ? endTime : null);

/**
 * Return whether the FOND-generated UG and leadin layers are out of date with respect to their inputs.
 *
 * A generated layer is one that hasn't been modified since the generation that last wrote it finished; layers the user has
 * modified since are never stale.
 */
export const getStaleness = ({
  layerLastModified = {},
  ugGenerationEndTime,
  leadinGenerationEndTime,
  legacyDataPrepEndTime,
  architectureLastModifiedAt,
}: StalenessInput): Staleness => {
  const ugBaseline = latest(ugGenerationEndTime, legacyDataPrepEndTime);
  const leadinBaseline = latest(leadinGenerationEndTime, legacyDataPrepEndTime);
  const lastModified = (layerId: LayerId) => toMillis(layerLastModified[layerId]);
  const architectureModified = toMillis(architectureLastModifiedAt);

  const modifiedSince = (layerIds: LayerId[], baseline: number) =>
    layerIds.some((layerId) => (lastModified(layerId) ?? -Infinity) > baseline) || (architectureModified ?? -Infinity) > baseline;

  const ugModified = lastModified(LayerIds.inStreet);
  const ugWriteBaseline = latest(ugGenerationEndTime, leadinGenerationEndTime, legacyDataPrepEndTime);
  const ugStale =
    ugBaseline !== null &&
    ugModified !== null &&
    ugWriteBaseline !== null &&
    ugModified <= ugWriteBaseline &&
    modifiedSince(UG_INPUT_LAYER_IDS, ugBaseline);

  const leadinsModified = lastModified(LayerIds.inLeadIns);
  const leadinsStale =
    leadinBaseline !== null && leadinsModified !== null && leadinsModified <= leadinBaseline && modifiedSince(LEADIN_INPUT_LAYER_IDS, leadinBaseline);

  return { ugStale, leadinsStale };
};
